import { useState, useEffect, useCallback, useMemo } from 'react';
import { notification, DatePicker } from 'antd';
import service from "../../services/Service";
import dayjs from 'dayjs';
import { GetColumnLiteralSearchProps } from '../../utils/functions';
import DataTable from '../DataTable/DataTable';
import { withLocale } from '../../l10n';
import { formatDateTime } from '../../utils/dates';

const { RangePicker } = DatePicker;

function ActivitiesTable({ lang }) {

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);

  // ===== 2. SELECTORES / CONSTANTES =====
  const ACTIVITY_ACTIONS = useMemo(() => [
    { text: lang.handle("Creación"), value: 'create' },
    { text: lang.handle("Actualización"), value: 'update' },
    { text: lang.handle("Borrado"), value: 'delete' },
    { text: lang.handle("Cambio de estado"), value: 'change-status' },
    { text: lang.handle("Cambio de contraseña"), value: 'change-password' },
    { text: lang.handle("Sincronización"), value: 'sync' },
    { text: lang.handle("Comienzo Personificación"), value: 'impersonate-on' },
    { text: lang.handle("Fin Personificación"), value: 'impersonate-off' },
  ], [lang]);

  const ACTIVITY_TYPES = useMemo(() => [
    { text: "Kibox", value: 'board' },
    { text: "Kiwi", value: 'kiwi' },
    { text: lang.handle("Usuario"), value: 'user' },
    { text: lang.handle("Pedido"), value: 'order' },
    { text: lang.handle("Orden de trabajo"), value: 'workorder' },
    { text: "M2M", value: 'm2m' },
    { text: "VPN", value: 'vpn' },
    { text: lang.handle("Rol"), value: 'role' },
    { text: lang.handle("Privilegio"), value: 'privilege' },
    { text: lang.handle("Organización"), value: 'tenant' },
    { text: lang.handle("Clave API"), value: 'apikey' },
    { text: lang.handle("Suscripción M2M"), value: 'm2msubscription' },
    { text: lang.handle("Suscripción de plan"), value: 'plansubscription' },
  ], [lang]);

  // ===== 4. HELPERS =====
  const getDate = (date) => dayjs(date).toISOString();

  const getTextAction = useCallback((value) => {
    return ACTIVITY_ACTIONS.find(el => el.value === value)?.text || value;
  }, [ACTIVITY_ACTIONS]);

  const getTextType = useCallback((value) => {
    return ACTIVITY_TYPES.find(el => el.value === value)?.text || value;
  }, [ACTIVITY_TYPES]);

  const getIdentifier = useCallback((value) => {
    if (value && value.includes('list')) {
      return lang.handle('Listado');
    }
    return value;
  }, [lang]);

  // ===== 5. FUNCIONES PRINCIPALES =====
  const loadData = useCallback(async (date) => {
    setLoading(true);
    try {
      const res = await service.post("activities", date);
      setData(res.data);
      notification["success"]({ message: lang.handle("Registros Cargados") });
    } catch (err) {
      console.error("Error loading activities:", err);
    } finally {
      setLoading(false);
    }
  }, [lang]);

  // ===== 6. HANDLERS =====
  const onRangeChange = (dates) => {
    if (!dates?.[0] || !dates?.[1]) return;
    
    loadData({ from: getDate(dates[0]), until: getDate(dates[1].endOf('minute')) });
  };

  // ===== 7. EFFECTS =====
  useEffect(() => {
    const date = {
      from: getDate(dayjs().subtract(7, 'day')),
      until: getDate(dayjs())
    };
    loadData(date);
  }, [loadData]);

  // ===== 8. CONFIG (columnas, etc) =====
  const columns = useMemo(() => [
    {
      title: lang.handle('Fecha'),
      dataIndex: 'create_ts',
      width: "10%",
      align: "center",
      render: (data) => <p> {formatDateTime(data)} </p>,
    },
    {
      title: lang.handle("Nombre de usuario"),
      dataIndex: ["metadata"],
      width: "10%",
      align: "center",
      ...GetColumnLiteralSearchProps(["metadata", "agent_name"], lang),
      render: (data) => {
        let name = data.agent_name;
        if (data.api_key) {
          name += ` (API ${data.api_key_name})`;
        }
        return name;
      }
    },
    {
      title: lang.handle('Acción'),
      dataIndex: 'operation',
      width: "10%",
      filters: ACTIVITY_ACTIONS,
      onFilter: (value, record) => record.operation === value,
      render: (data) => getTextAction(data)
    },
    {
      title: lang.handle('Tipo de objeto'),
      dataIndex: ["metadata", "type"], width: "10%",
      filters: ACTIVITY_TYPES,
      onFilter: (value, record) => record.metadata.type === value,
      render: (data) => getTextType(data)
    },
    {
      title: lang.handle('Identificador'),
      dataIndex: 'target',
      ...GetColumnLiteralSearchProps("target", lang),
      render: (data) => getIdentifier(data)
    },
    {
      title: lang.handle('Nombre'),
      dataIndex: ['metadata', 'target_name'],
      ...GetColumnLiteralSearchProps(["metadata", "target_name"], lang)
    },
  ], [lang, ACTIVITY_ACTIONS, ACTIVITY_TYPES, getTextAction, getTextType, getIdentifier]);

  const rangePresets = useMemo(() => [
    {
      label: lang.handle('Hace 3 horas'),
      value: [dayjs().subtract(3, 'hour'), dayjs()],
    },
    {
      label: lang.handle('Hace 1 día'),
      value: [dayjs().subtract(1, 'day'), dayjs()],
    },
    {
      label: lang.handle('Hace 3 días'),
      value: [dayjs().subtract(3, 'day'), dayjs()],
    },
    {
      label: lang.handle('Hace 1 semana'),
      value: [dayjs().subtract(1, 'week'), dayjs()],
    }
  ], [lang]);

  // ===== 9. RENDER =====
  return (
    <div className='card card-body'>
      <div className="row mt-4 mb-3">
        <div className="col-6">
          <RangePicker
            defaultValue={[dayjs().subtract(7, 'day'), dayjs()]}
            presets={rangePresets}
            style={{ width: '100%' }}
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            size="large"
            onChange={onRangeChange}
          />
        </div>
      </div>
      <DataTable
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        scroll={{ x: true, y: 600 }}
        pagination={false}
      />
    </div>
  );
}

export default withLocale(ActivitiesTable);
